import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalPage } from "@/components/legal-page";

const TITLE = "Subprocessors";
const DESC =
  "The third-party vendors that can process Pyng customer data, what each one does, and what data it sees.";

export const Route = createFileRoute("/subprocessors")({
  head: () => ({
    meta: [
      { title: "Subprocessors — Pyng" },
      { name: "description", content: DESC },
      { property: "og:title", content: `${TITLE} — Pyng` },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: SubprocessorsPage,
});

const ROWS: Array<{ name: string; purpose: string; data: string }> = [
  {
    name: "Lovable Cloud",
    purpose: "Application hosting, database, authentication, file storage, and email delivery",
    data: "All customer data stored in Pyng, including contacts, notes, memories, and voice memo audio",
  },
  {
    name: "Google Gemini (via the Lovable AI Gateway)",
    purpose: "Drafting messages, summarizing relationships, extracting facts from voice notes",
    data: "Only the specific text needed for one request. Not retained for model training",
  },
  {
    name: "Firecrawl",
    purpose: "Fetching and reading public web pages for contacts you add",
    data: "Public URLs and the name or company of the contact being researched. No private notes",
  },
  {
    name: "Google (Calendar and Gmail APIs)",
    purpose: "Live Context (meeting prep, post-meeting hooks, last touch), only if you connect it",
    data: "Calendar events and email metadata from your own Google account, at your explicit consent",
  },
  {
    name: "Apple",
    purpose: "Sign in with Apple and iOS app distribution",
    data: "Authentication identifiers only",
  },
];

function SubprocessorsPage() {
  return (
    <LegalPage title={TITLE} eyebrow="Trust" lastUpdated="August 2026">
      <p>
        These are the third parties that can process customer data on Pyng's behalf. We keep this list short
        on purpose, and we will update this page before adding a new subprocessor that touches your content.
      </p>

      <div className="not-prose overflow-hidden rounded-2xl border border-border/60">
        {ROWS.map((r, i) => (
          <div
            key={r.name}
            className={`grid gap-2 p-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1.3fr)] sm:gap-4 ${
              i > 0 ? "border-t border-border/60" : ""
            }`}
          >
            <p className="text-[14px] font-medium text-foreground">{r.name}</p>
            <p className="text-[13px] leading-relaxed text-muted-foreground">{r.purpose}</p>
            <p className="text-[13px] leading-relaxed text-muted-foreground">{r.data}</p>
          </div>
        ))}
      </div>

      <h2>What none of them get</h2>
      <ul>
        <li>We do not sell customer data or share it with advertisers or data brokers.</li>
        <li>No analytics or advertising vendor receives your contacts, notes, or memories.</li>
        <li>No vendor on this list is permitted to use your content to train its own models.</li>
      </ul>

      <h2>Questions</h2>
      <p>
        For a data processing agreement or a vendor review, email{" "}
        <a href="mailto:security@pyng.co">security@pyng.co</a>. See also our{" "}
        <Link to="/security">security overview</Link> and{" "}
        <Link to="/privacy">privacy policy</Link>.
      </p>
    </LegalPage>
  );
}